import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useEmployees, useDepartments, useImportBrightHr, useHrContext } from '@/hooks/use-api'
import { apiClient } from '@/lib/api'
import { useToast } from '@/hooks/use-toast'
import { Users, Plus, Search, Upload, Download } from 'lucide-react'
import { EmployeeForm } from '@/components/hr/EmployeeForm'
import type { EmploymentStatus } from '@/types/hr'
import { explainError } from '@/lib/explain-error'

const STATUS_FILTERS: Array<EmploymentStatus | 'All'> = ['All', 'Active', 'On Leave', 'Terminated']

function getInitials(first?: string | null, last?: string | null): string {
  return `${(first ?? '').charAt(0)}${(last ?? '').charAt(0)}`.toUpperCase() || '?'
}

function statusBadgeVariant(
  status: EmploymentStatus
): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'Active':
      return 'default'
    case 'On Leave':
      return 'secondary'
    case 'Terminated':
      return 'outline'
    default:
      return 'outline'
  }
}

export default function EmployeesPage() {
  const navigate = useNavigate()
  const { data: hrCtx } = useHrContext()
  const isHrAdmin = hrCtx?.isHrAdmin ?? false
  const { toast } = useToast()
  const [search, setSearch] = useState('')
  const [departmentId, setDepartmentId] = useState<string>('all')
  const [statusFilter, setStatusFilter] = useState<EmploymentStatus | 'All'>('Active')
  const [createOpen, setCreateOpen] = useState(false)
  const [exportOpen, setExportOpen] = useState(false)
  const [exporting, setExporting] = useState(false)
  const brightHrInputRef = useRef<HTMLInputElement>(null)
  const importBrightHr = useImportBrightHr()

  const handleExport = async () => {
    setExporting(true)
    try {
      const { url } = await apiClient.getHrExportLink()
      // Navigate the browser to the token-gated URL so the (large) ZIP streams
      // straight to disk rather than buffering in memory.
      const a = document.createElement('a')
      a.href = url
      a.download = ''
      document.body.appendChild(a)
      a.click()
      a.remove()
      setExportOpen(false)
    } catch {
      toast({
        title: 'Export failed',
        description: 'Could not start the HR export.',
        variant: 'destructive',
      })
    } finally {
      setExporting(false)
    }
  }

  const handleBrightHrFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    try {
      const csv = await file.text()
      const s = await importBrightHr.mutateAsync(csv)
      toast({
        title: 'BrightHR import complete',
        description: `${s.created} added, ${s.updated} updated, ${s.emergencyContacts} emergency contacts, ${s.sensitiveRows} sensitive records, ${s.managersResolved} reporting lines.`,
      })
      if (s.warnings.length > 0) {
        toast({
          title: `${s.warnings.length} warning${s.warnings.length === 1 ? '' : 's'}`,
          description: s.warnings.slice(0, 8).join(' • '),
        })
      }
    } catch (err: any) {
      toast({
        title: 'Import failed',
        description: explainError(err, 'Could not import the BrightHR CSV.'),
        variant: 'destructive',
      })
    }
  }

  const { data, isLoading, error } = useEmployees({
    search: search.trim() || undefined,
    department_id: departmentId === 'all' ? undefined : departmentId,
    employment_status: statusFilter === 'All' ? undefined : statusFilter,
    limit: 200,
  })
  const { data: departments } = useDepartments()

  const employees = data?.data ?? []
  const total = data?.total ?? 0

  return (
    <main className="container mx-auto p-6 max-w-7xl">
      <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Users className="h-6 w-6" aria-hidden="true" />
            Employees
          </h1>
          <p className="text-muted-foreground text-sm">
            {total} {total === 1 ? 'person' : 'people'} in the directory
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isHrAdmin && (
            <Button
              variant="outline"
              onClick={() => setExportOpen(true)}
              aria-label="Download a complete export of all HR data"
            >
              <Download className="h-4 w-4 mr-2" aria-hidden="true" />
              Export all HR data
            </Button>
          )}
          {isHrAdmin && (
            <>
              <input
                ref={brightHrInputRef}
                type="file"
                accept=".csv,text/csv"
                className="sr-only"
                aria-hidden="true"
                tabIndex={-1}
                onChange={handleBrightHrFile}
              />
              <Button
                variant="outline"
                onClick={() => brightHrInputRef.current?.click()}
                disabled={importBrightHr.isPending}
                aria-label="Import employees from a BrightHR CSV export"
              >
                <Upload className="h-4 w-4 mr-2" aria-hidden="true" />
                {importBrightHr.isPending ? 'Importing…' : 'Import from BrightHR'}
              </Button>
            </>
          )}
          {isHrAdmin && (
            <Button onClick={() => setCreateOpen(true)} aria-label="Create new employee">
              <Plus className="h-4 w-4 mr-2" aria-hidden="true" />
              New Employee
            </Button>
          )}
        </div>
      </header>

      <Card className="mb-4">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium">Filters</CardTitle>
          <CardDescription>Search by name, email, or job title</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="relative">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"
                aria-hidden="true"
              />
              <Input
                aria-label="Search employees"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search…"
                className="pl-9"
              />
            </div>
            <Select value={departmentId} onValueChange={setDepartmentId}>
              <SelectTrigger aria-label="Filter by department">
                <SelectValue placeholder="Department" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All departments</SelectItem>
                {(departments ?? []).map((d) => (
                  <SelectItem key={d.id} value={d.id}>
                    {d.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {/* Status filter is HR-admin only; everyone else sees the active
                directory (the backend clamps non-admins to Active). */}
            {isHrAdmin && (
              <Select
                value={statusFilter}
                onValueChange={(v) => setStatusFilter(v as EmploymentStatus | 'All')}
              >
                <SelectTrigger aria-label="Filter by status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_FILTERS.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </CardContent>
      </Card>

      {isLoading && (
        <div role="status" aria-live="polite" className="text-center py-12 text-muted-foreground">
          Loading employees…
        </div>
      )}

      {error && (
        <div role="alert" className="text-center py-12 text-destructive">
          Failed to load employees.
        </div>
      )}

      {!isLoading && !error && employees.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <p>No employees match these filters.</p>
            {isHrAdmin && (
              <Button className="mt-4" onClick={() => setCreateOpen(true)}>
                <Plus className="h-4 w-4 mr-2" aria-hidden="true" />
                Add your first employee
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {!isLoading && employees.length > 0 && (
        <ul
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"
          aria-label="Employee directory"
        >
          {employees.map((e) => {
            const display = e.preferred_name || e.first_name
            return (
              <li key={e.id}>
                <button
                  type="button"
                  className="w-full text-left p-4 rounded-lg border bg-card hover:bg-muted hover:border-primary/30 focus:outline-none focus:ring-2 focus:ring-ring transition-colors"
                  onClick={() => navigate(`/hr/employees/${e.id}`)}
                  aria-label={`View ${display} ${e.last_name}`}
                >
                  <div className="flex items-start gap-3">
                    {e.profile_photo_url ? (
                      <img
                        src={e.profile_photo_url}
                        alt=""
                        className="h-12 w-12 rounded-full object-cover bg-muted"
                      />
                    ) : (
                      <div
                        className="h-12 w-12 rounded-full bg-primary/10 text-primary flex items-center justify-center font-semibold"
                        aria-hidden="true"
                      >
                        {getInitials(display, e.last_name)}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold truncate">
                          {display} {e.last_name}
                        </p>
                        <Badge
                          variant={statusBadgeVariant(e.employment_status)}
                          aria-label={`Status: ${e.employment_status}`}
                        >
                          {e.employment_status}
                        </Badge>
                      </div>
                      {e.job_title && (
                        <p className="text-sm text-muted-foreground truncate">{e.job_title}</p>
                      )}
                      {e.department_name && (
                        <p className="text-xs text-muted-foreground truncate">
                          {e.department_name}
                        </p>
                      )}
                      {e.work_email && (
                        <p className="text-xs text-primary truncate mt-1">
                          <span className="sr-only">Email: </span>
                          {e.work_email}
                        </p>
                      )}
                    </div>
                  </div>
                </button>
              </li>
            )
          })}
        </ul>
      )}

      <EmployeeForm open={createOpen} onOpenChange={setCreateOpen} />

      <AlertDialog open={exportOpen} onOpenChange={(o) => !o && setExportOpen(false)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Export all HR data?</AlertDialogTitle>
            <AlertDialogDescription>
              This downloads a single ZIP with every employee’s details and documents, departments,
              emergency contacts, time-off, the company document library, and the{' '}
              <strong>decrypted sensitive vault (salary, SIN, banking, passport)</strong>. It’s a
              large file, contains highly sensitive personal data, and the export is logged. Store
              it securely and delete it when no longer needed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={exporting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault()
                handleExport()
              }}
              disabled={exporting}
            >
              {exporting ? 'Preparing…' : 'Download export'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </main>
  )
}
